import { customElement, property, state } from "lit/decorators.js";
import { LitElementBase } from "../LitElementBase";
import { css, html, nothing } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { createContext, consume, provide } from "@lit/context";

interface INavTreeContext {
  endpoint?: string;
  currentPageLink?: string;
  maxDepth?: string;
}

export const navTreeContext = createContext<INavTreeContext>("ehm-navtree");

/**
 * Root navigation tree component.
 *
 * Provides context with:
 * - `endpoint` (from `data-endpoint`)
 * - `currentPageLink` (from `data-current-pageref`)
 *
 * Slots:
 * - default: nav tree items.
 */
@customElement("ehm-navtree")
export class NavTree extends LitElementBase {

  @provide({ context: navTreeContext })
  navTreeContext = {
    endpoint: this.dataset.endpoint,
    currentPageLink: this.dataset.currentPageref,
    maxDepth: this.dataset.maxdepth
  };

  constructor() {
    super();
  }

  render() {
    return html`
      <nav part="nav">
        <slot></slot>
      </nav>
    `;
  }
}

/**
 * Navigation tree item component.
 *
 * Attributes:
 * - `has-children` (boolean): Whether this item has server/DOM-provided children.
 * - `expanded` (boolean, reflected): Controls visibility of the children slot.
 * - `data-pageref` (string): Parent page reference used in fetch queries.
 * - `data-level` (string|number): Current nesting level used in fetch queries.
 *
 * Slots:
 * - default: item label/content.
 * - `name="children"`: child items. If empty and `has-children` is true, they can be fetched on expand.
 */
@customElement("ehm-navtree-item")
export class NavTreeItem extends LitElementBase {
  static styles = css`
    button {
      appearance: none;
      border: none;
      background-color: transparent;
      width: 3.5rem;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      position: relative;
    }
    button:focus-visible {
      outline: none !important;
      &:after {
        position: absolute;
        inset: 0.5rem;
        content: "";
        display: block;
        outline: var(--outline);
        border-radius: var(--radius-sm);
      }
    }
    .button__inner {
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: var(--navtree-expander-bg);
      width: 1.5rem;
      height: 1.5rem;
      border-radius: 4px;
      transition: background-color 150ms ease-in-out;
      --icon-size: 1rem;
      --icon-color: var(--navtree-expander-fg);
      --icon-rotate: 90deg;
    }
    :host([expanded]) .button__inner {
      background-color: var(--navtree-expander-bg-expanded);
      --icon-color: var(--navtree-expander-fg-expanded);
      --icon-rotate: -90deg;
    }
    button:hover .button__inner {
      background-color: var(--navtree-expander-bg-hover);
    }
    slot {
      display: block;
      flex-grow: 1;
    }
    .inner {
      display: flex;
      justify-content: space-between;
      button{
      flex: 0;
      padding-right: 1rem;
      }
    }
    .hidden {
      display: none;
    }
  `;

  @property({ type: Boolean, attribute: "has-children" }) hasChildren = false;
  @property({ type: Boolean, attribute: "expanded", reflect: true }) expanded = false;

  @consume({ context: navTreeContext }) private context?: INavTreeContext;

  @state() private childMarkup: string | null = null;

  constructor() {
    super();
  }

  render() {
    let expandButton;
    if (this.hasChildren) {
      expandButton = html`
        <button
          aria-expanded=${this.expanded ? "true" : "false"}
          aria-controls="children"
          aria-label="${this.expanded ? "-" : "+"}"
          @click=${this.toggle}
        >
          <span class="button__inner">
            <ehm-icon name="caret"></ehm-icon>
          </span>
        </button>
      `;
    }
    return html`
      <div class="inner">
        <slot></slot>
        ${expandButton}
      </div>
      <slot name="children" id="children" class=${this.expanded ? nothing : "hidden"}>
        ${this.childMarkup ? unsafeHTML(this.childMarkup) : nothing}
      </slot>
    `;
  }

  async toggle() {
    if (!this.expanded) {
      this.expanded = true;
      await this.fetchChildren();
      return;
    }
    this.expanded = false;
  }

  async fetchChildren() {
    const slot = this.shadowRoot?.querySelector("slot[name=children]") as HTMLSlotElement;
    // If no children, do not fetch
    if (!this.hasChildren) return;
    // If children slot are already assigned from server (in cshtml view), do not fetch
    if (slot?.assignedNodes().length > 0) return;
    // If children are already fetched, do not fetch again
    if (!this.context?.endpoint || this.childMarkup !== null) return;
    const response = await fetch(
      `${this.context?.endpoint}?currentPageId=${this.context?.currentPageLink}&parentId=${this.dataset.pageref}&currentLevel=${this.dataset.level}&maxDepth=${this.context?.maxDepth}`,
      {
        headers: {
          "Accept-language": document.documentElement.lang,
        },
      },
    );
    const json = await response.json();
    this.childMarkup = json.html;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ehm-navtree": NavTree;
    "ehm-navtree-item": NavTreeItem;
  }
}

export {};
